import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/lib';
import { radius } from '@/shared/theme';
import { Icon, type IconName } from './icon';

export type PageItem = number | 'gap-start' | 'gap-end';

/**
 * Production's page window: up to `max` numbers starting one before the current page, plus the
 * first and last page behind an ellipsis when they fall outside it.
 */
export function pageWindow(current: number, pages: number, max: number): PageItem[] {
  let start = Math.max(1, current - 1);
  const end = Math.min(pages, start + max - 1);
  if (end - start < max - 1) start = Math.max(1, end - max + 1);

  const items: PageItem[] = [];
  if (start > 1) {
    items.push(1);
    if (start > 2) items.push('gap-start');
  }
  for (let i = start; i <= end; i++) items.push(i);
  if (end < pages) {
    if (end < pages - 1) items.push('gap-end');
    items.push(pages);
  }
  return items;
}

interface NumberedPagerProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  pageSizes: readonly number[];
  onPageSizeChange: (size: number) => void;
}

/**
 * The old dashboard's table footer: rows-per-page select on the left, "Showing 1 to 10 of 42
 * results" in the middle, and "Page 1 of 5" with first/previous/numbered/next/last buttons on the
 * right. Narrow screens get the centred page buttons only, as production did.
 *
 * Production hid the footer on a single page, which also hid the select; it stays here while a
 * larger page size than the smallest is chosen, so that choice can be undone.
 */
export function NumberedPager({
  page,
  pageSize,
  total,
  onPageChange,
  pageSizes,
  onPageSizeChange,
}: NumberedPagerProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const labelId = useId();

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pages);
  const smallest = Math.min(...pageSizes);
  if (total === 0 || (pages <= 1 && pageSize <= smallest)) return null;

  const from = (current - 1) * pageSize + 1;
  const to = Math.min(total, current * pageSize);
  const size = compact ? 36 : 28;

  const nav = (icon: IconName, label: string, target: number, disabled: boolean) => (
    <PageButton size={size} label={label} disabled={disabled} onClick={() => onPageChange(target)}>
      <Icon name={icon} size={compact ? 18 : 16} />
    </PageButton>
  );

  const buttons = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      {nav('chevronsL', t('pager.first'), 1, current === 1)}
      {nav('chevronL', t('pager.previous'), current - 1, current === 1)}
      {pageWindow(current, pages, compact ? 3 : 5).map((item) =>
        typeof item === 'number' ? (
          <PageButton
            key={item}
            size={size}
            label={t('pager.goTo', { page: item })}
            active={item === current}
            onClick={() => onPageChange(item)}
          >
            {fmt.number(item)}
          </PageButton>
        ) : (
          <Box
            key={item}
            component="span"
            aria-hidden
            sx={{ width: size, textAlign: 'center', color: 'text.secondary' }}
          >
            ...
          </Box>
        ),
      )}
      {nav('chevronR', t('pager.next'), current + 1, current === pages)}
      {nav('chevronsR', t('pager.last'), pages, current === pages)}
    </Box>
  );

  const pageOf = (
    <Box component="span" sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
      {t('pager.page', { page: fmt.number(current), pages: fmt.number(pages) })}
    </Box>
  );

  return (
    <Box
      component="nav"
      aria-label={t('pager.label')}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: compact ? 'column' : 'row',
        gap: compact ? 1.5 : 2,
        px: compact ? 2 : 3,
        py: 2,
        background: theme.vars.palette.md.panel,
        borderTop: `1px solid ${theme.vars.palette.md.border}`,
        color: theme.vars.palette.md.muted,
      })}
    >
      {compact ? (
        <>
          {pageOf}
          {buttons}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              labelId={labelId}
              sx={{ width: 80 }}
            >
              {pageSizes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Box component="span" id={labelId} sx={{ fontSize: 14 }}>
              {t('pager.rowsPerPage')}
            </Box>
          </Box>
          <Box component="span" sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
            {t('pager.showing', {
              start: fmt.number(from),
              end: fmt.number(to),
              total: fmt.number(total),
            })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {pageOf}
            {buttons}
          </Box>
        </>
      )}
    </Box>
  );
}

interface PageButtonProps {
  size: number;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function PageButton({ size, label, active, disabled, onClick, children }: PageButtonProps) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      sx={(theme) => ({
        width: size,
        height: size,
        flex: 'none',
        borderRadius: radius.control,
        fontSize: 14,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        border: `1px solid ${active ? theme.vars.palette.md.primary : theme.vars.palette.md.border}`,
        background: active ? theme.vars.palette.md.primary : theme.vars.palette.md.panel,
        color: active ? theme.vars.palette.primary.contrastText : theme.vars.palette.md.muted,
        transition: 'background .15s ease',
        '&:hover': active ? {} : { background: theme.vars.palette.md.panel2 },
        '&.Mui-disabled': { opacity: 0.5 },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.vars.palette.md.primary}`,
          outlineOffset: 1,
        },
      })}
    >
      {children}
    </ButtonBase>
  );
}
